import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import type { Client } from "@shared/schema";
import { clampPosition, type InterviewPosition } from "@shared/interview/chapters";
import { INTERVIEW_SHELL_ENABLED } from "@/lib/features";

/**
 * INTERVIEW-SHELL-1 (s205) — which interview rung, if any, the gate ladder should render.
 *
 * The ladder in `App.tsx` is a series of early returns, so a rung cannot "fall through" while it
 * decides. This hook makes the decision ahead of the return, and reports `loading` explicitly so the
 * ladder can hold the spinner it already shows rather than flashing the dashboard and then replacing
 * it with a full-screen interview.
 *
 * 🔴 The client record is fetched ONLY for role `client`. Advisors, executors and administrators have
 * no client row of their own, and adding an unconditional fetch to the ladder would put a request in
 * front of every login in the product for a surface three of the four roles never see.
 *
 * Same query key and fetch as `ClientDashboard` (`["/api/clients", user.id]`), so TanStack dedupes it
 * — the ladder and the dashboard share one fetch rather than racing two.
 */
export type InterviewGate =
  | { status: "loading" }
  /** Not a planner, or the interview is already finished — the ladder proceeds to AppShell. */
  | { status: "none" }
  /** Never started: show the mode intro (D-202.24). */
  | { status: "intro"; clientData: Client }
  /** In progress: show the shell at the saved position. */
  | { status: "shell"; clientData: Client; position: InterviewPosition }
  /** s228 (PLAN-HOME-1): finished — the plan's home (board 12), behind the same flag. */
  | { status: "done"; clientData: Client };

export function useInterviewGate(user: { id?: string; role?: string } | null | undefined): InterviewGate {
  const isPlanner = user?.role === "client";

  const { data: clients, isLoading } = useQuery<Client[]>({
    queryKey: ["/api/clients", user?.id],
    queryFn: async () => (await apiRequest("GET", "/api/clients")).json(),
    // The flag also switches the fetch off: with the shell hidden there is nothing to decide,
    // so production does not pay for a request in front of every planner login.
    enabled: !!isPlanner && !!user?.id && INTERVIEW_SHELL_ENABLED,
  });

  // INTERVIEW-SHELL-FLAG-1 (s206) — checked FIRST, and deliberately so: this is a kill switch, not
  // one more condition. `none` here makes both mounts in App.tsx unreachable, which is the whole
  // mechanism. It runs after the hooks above because hook order must not vary between renders.
  //
  // 🔴 This must stay ahead of the interviewPosition branches. A planner mid-interview is exactly
  // the case the flag has to cover — gating only the `intro` branch would leave anyone with a saved
  // position stuck in the shell on production.
  if (!INTERVIEW_SHELL_ENABLED) return { status: "none" };

  if (!isPlanner) return { status: "none" };
  if (isLoading || !clients) return { status: "loading" };

  const clientData = clients[0];
  if (!clientData) return { status: "none" };

  // Finished wins over position: a completed interview never re-opens on its own. s228: with the flag on, a finished
  // planner's home is the plan's own (board 12, rule 127), not the old dashboard.
  if (clientData.interviewCompletedAt) return { status: "done", clientData };

  const raw = clientData.interviewPosition as InterviewPosition | null | undefined;
  if (!raw) return { status: "intro", clientData };

  // A stored cursor can outlive the chapter model it was written against (see clampPosition).
  return { status: "shell", clientData, position: clampPosition(raw) };
}
